// Open-application counts for the admin sidebar badges: how many
// PENDING creator applications ("Creators" nav item) and pending
// portfolio applications ("Portfolio applications" nav item) await
// review. Both list endpoints already compute `total_pending`, so each
// count is a page_size=1 probe of the existing endpoint — no new
// server surface. Null (endpoint hiccup / older backend) renders as no
// badge rather than an error.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

async function fetchTotalPending(url: string): Promise<number | null> {
  const response = await fetch(url, { credentials: "include" });
  attachRequestIdToScope(response);
  if (!response.ok) return null;
  const raw = (await response.json()) as { total_pending?: number | null };
  return typeof raw.total_pending === "number" ? raw.total_pending : null;
}

export function useAdminOpenApplicationCounts(): {
  creatorApplications: number | null;
  portfolioApplications: number | null;
} {
  const creator = useQuery({
    queryKey: ["admin-open-counts", "creator-applications"],
    queryFn: () =>
      fetchTotalPending(
        "/api/v1/admin/creator-applications?status=pending&page=1&page_size=1",
      ),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
  const portfolio = useQuery({
    queryKey: ["admin-open-counts", "portfolio-applications"],
    queryFn: () =>
      fetchTotalPending(
        "/api/v1/admin/portfolio-applications?status=pending&page=1&page_size=1",
      ),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
  return {
    creatorApplications: creator.data ?? null,
    portfolioApplications: portfolio.data ?? null,
  };
}
